import React, { useCallback, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from '../../../../services/notificationService';
import type { RoleResponse, ScopeAssignmentItem, ScopeType } from 'api/types';
import {
  useRoleScopeAssignments,
  useUpdateRoleScopeDefault,
  useRemoveRoleScopeAssignments,
  useApplications,
} from 'hooks/api';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';

// ============================================================================
// TYPES
// ============================================================================

interface RoleViewModalProps {
  isOpen: boolean;
  role: RoleResponse | null;
  onClose: () => void;
  tabId?: string;
  moduleName?: string;
  onRestoreView?: (roleCode: string) => void;
}

// ============================================================================
// HELPERS
// ============================================================================

const SectionHeader: React.FC<{ icon: React.ReactNode; title: string; action?: React.ReactNode }> = ({
  icon, title, action,
}) => (
  <div style={{
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '0.5rem',
    padding: '0.4rem 0.75rem',
    background: 'var(--bg-secondary)',
    borderLeft: '3px solid var(--primary)',
    borderRadius: '0 0.25rem 0.25rem 0',
    marginBottom: '0.75rem',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-primary)',
  }}>
    <span style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
      {icon}
      {title}
    </span>
    {action}
  </div>
);

const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
    <div style={{ width: '160px', flexShrink: 0, fontWeight: 500, fontSize: '0.85rem', color: 'var(--text-secondary)' }}>
      {label}
    </div>
    <div style={{
      flex: 1,
      fontSize: '0.85rem',
      color: 'var(--text-primary)',
      background: 'var(--bg-secondary)',
      border: '1px solid var(--form-border-color)',
      borderRadius: '0.375rem',
      padding: '0.375rem 0.625rem',
      minHeight: '2rem',
      display: 'flex',
      alignItems: 'center',
    }}>
      {value || '-'}
    </div>
  </div>
);

/**
 * One scope group (Entities / Business Units / Financial Years).
 * The API only returns items already assigned to the role, so this lists them
 * and lets the user promote any of them to be the default.
 */
const ScopeGroup: React.FC<{
  title: string;
  scopeType: ScopeType;
  items: ScopeAssignmentItem[];
  busy: boolean;
  onSetDefault: (scopeType: ScopeType, item: ScopeAssignmentItem) => void;
}> = ({ title, scopeType, items, busy, onSetDefault }) => (
  <div style={{ marginBottom: '0.75rem' }}>
    <div style={{ fontSize: '0.75rem', fontWeight: 600, color: 'var(--text-secondary)', marginBottom: '0.375rem' }}>
      {title}
    </div>
    {items.length === 0 ? (
      <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.25rem 0' }}>
        No {title.toLowerCase()} assigned.
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem' }}>
        {items.map(item => (
          <div
            key={item.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '0.5rem',
              padding: '0.375rem 0.625rem',
              border: '1px solid var(--border-color)',
              borderRadius: '0.375rem',
              fontSize: '0.8rem',
            }}
          >
            <span>
              <strong>{item.code}</strong>
              {item.name ? ` — ${item.name}` : ''}
            </span>
            {item.isDefault ? (
              <span className="badge badge-active" style={{ fontSize: '0.7rem' }}>Default</span>
            ) : (
              <button
                className="btn btn-secondary"
                style={{ padding: '0.125rem 0.5rem', fontSize: '0.7rem' }}
                disabled={busy}
                onClick={() => onSetDefault(scopeType, item)}
              >
                Set default
              </button>
            )}
          </div>
        ))}
      </div>
    )}
  </div>
);

// ============================================================================
// COMPONENT
// ============================================================================

const RoleViewModal: React.FC<RoleViewModalProps> = ({
  isOpen,
  role,
  onClose,
  tabId,
  moduleName,
  onRestoreView,
}) => {
  const { minimize } = useModalMinimize();
  const { data: applications = [] } = useApplications();

  // Scope assignments are tenant-scoped; the hook surfaces a message when the
  // API declines (e.g. no tenant context on the token).
  const { data: scopes, loading: scopesLoading, message: scopesMessage, refetch: refetchScopes } =
    useRoleScopeAssignments(isOpen && role ? role.id : null);
  const { updateScopeDefault, loading: settingDefault } = useUpdateRoleScopeDefault();
  const { removeScopeAssignments, loading: removingScopes } = useRemoveRoleScopeAssignments();
  const [confirmRemoveScopes, setConfirmRemoveScopes] = useState(false);

  const handleMinimize = useCallback(() => {
    if (!tabId || !moduleName || !role) return;
    minimize({
      id: `${tabId}-view-modal-${role.code}`,
      title: `View - ${role.code}`,
      moduleName,
      tabId,
      onRestore: () => onRestoreView?.(role.code),
    });
    onClose();
  }, [tabId, moduleName, role, minimize, onClose, onRestoreView]);

  const handleSetDefault = useCallback(async (scopeType: ScopeType, item: ScopeAssignmentItem) => {
    if (!role) return;
    try {
      await updateScopeDefault(role.id, { scopeType, scopeValueId: item.id }, {
        onSuccess: () => {
          notificationService.toast.success(`"${item.code}" is now the default ${scopeType}.`);
          refetchScopes();
        },
      });
    } catch (err: any) {
      notificationService.toast.error(
        err?.response?.data?.message || err?.message || 'Failed to update the default scope.',
      );
    }
  }, [role, updateScopeDefault, refetchScopes]);

  const handleRemoveScopes = useCallback(async () => {
    if (!role) return;
    try {
      await removeScopeAssignments(role.id, {
        onSuccess: () => {
          setConfirmRemoveScopes(false);
          notificationService.toast.success('Scope assignments removed.');
          refetchScopes();
        },
      });
    } catch (err: any) {
      setConfirmRemoveScopes(false);
      notificationService.toast.error(
        err?.response?.data?.message || err?.message || 'Failed to remove scope assignments.',
      );
    }
  }, [role, removeScopeAssignments, refetchScopes]);

  if (!isOpen || !role) return null;

  const app = applications.find(a => String(a.appId) === String(role.appId));
  const hasScopes = !!scopes &&
    (scopes.entities.length > 0 || scopes.businessUnits.length > 0 || scopes.financialYears.length > 0);
  const scopeBusy = settingDefault || removingScopes;

  return (
    <>
      <div className="modal-backdrop" onClick={onClose} />
      <div className="modal-container modal-lg">
        {/* Header */}
        <div className="modal-header">
          <h2 className="modal-title">
            <IconSystem.lock size={20} />
            Role Details
          </h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}>
            {tabId && (
              <button className="modal-header-btn" onClick={handleMinimize} title="Minimize to bar">
                <IconSystem.chevronDown size={16} />
              </button>
            )}
            <button className="modal-close" onClick={onClose}>
              <IconSystem.x size={20} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="modal-content">
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader icon={<IconSystem.info size={16} />} title="General" />
            <DetailRow label="Role ID" value={<span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{role.id}</span>} />
            <DetailRow label="Code" value={role.code} />
            <DetailRow label="Name" value={role.name} />
            <DetailRow label="Role Type" value={role.roleType} />
            <DetailRow label="Description" value={role.description} />
            <DetailRow
              label="Application"
              value={app ? `${app.appCode} — ${app.appName}` : role.appId ? String(role.appId) : 'None'}
            />
            <DetailRow
              label="Composite"
              value={
                <span className={`badge ${role.isComposite ? 'badge-active' : 'badge-inactive'}`}>
                  {role.isComposite ? 'Yes' : 'No'}
                </span>
              }
            />
            <DetailRow
              label="Status"
              value={role.statusCode
                ? (
                  <span className={`badge ${role.statusCode.toLowerCase().includes('inactive') ? 'badge-inactive' : 'badge-active'}`}>
                    {role.statusName || role.statusCode}
                  </span>
                )
                : undefined}
            />
          </div>

          {role.attributes && Object.keys(role.attributes).length > 0 && (
            <div style={{ marginBottom: '1.5rem' }}>
              <SectionHeader icon={<IconSystem.settings size={16} />} title="Attributes" />
              <DetailRow
                label="Attributes"
                value={
                  <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordBreak: 'break-all', fontFamily: 'inherit', fontSize: 'inherit' }}>
                    {JSON.stringify(role.attributes, null, 2)}
                  </pre>
                }
              />
            </div>
          )}

          {/* Scope assignments (tenant-scoped) */}
          <div style={{ marginBottom: '1.5rem' }}>
            <SectionHeader
              icon={<IconSystem.folder size={16} />}
              title="Scope Assignments"
              action={hasScopes ? (
                <button
                  className="btn btn-secondary"
                  style={{ padding: '0.125rem 0.5rem', fontSize: '0.7rem' }}
                  disabled={scopeBusy}
                  onClick={() => setConfirmRemoveScopes(true)}
                >
                  Remove all
                </button>
              ) : undefined}
            />
            {scopesLoading ? (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.25rem 0' }}>
                Loading scope assignments...
              </div>
            ) : scopes ? (
              <>
                <ScopeGroup title="Entities" scopeType="Entity" items={scopes.entities} busy={scopeBusy} onSetDefault={handleSetDefault} />
                <ScopeGroup title="Business Units" scopeType="BusinessUnit" items={scopes.businessUnits} busy={scopeBusy} onSetDefault={handleSetDefault} />
                <ScopeGroup title="Financial Years" scopeType="FinancialYear" items={scopes.financialYears} busy={scopeBusy} onSetDefault={handleSetDefault} />
                <div style={{ marginTop: '0.5rem' }}>
                  <DetailRow label="Default Entity" value={scopes.defaults.defaultEntityName} />
                  <DetailRow label="Default Business Unit" value={scopes.defaults.defaultBusinessUnitName} />
                  <DetailRow label="Default Financial Year" value={scopes.defaults.defaultFinancialYearName} />
                </div>
              </>
            ) : (
              <div style={{ fontSize: '0.8rem', color: 'var(--text-muted)', padding: '0.25rem 0' }}>
                {scopesMessage || 'No scope assignments for this role.'}
              </div>
            )}
          </div>

          {/* Audit */}
          <div>
            <SectionHeader icon={<IconSystem.calendar size={16} />} title="Audit" />
            <DetailRow label="Created" value={role.createdAt ? new Date(role.createdAt).toLocaleString() : undefined} />
            <DetailRow label="Updated" value={role.updatedAt ? new Date(role.updatedAt).toLocaleString() : undefined} />
          </div>
        </div>

        {/* Footer */}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Close</button>
        </div>
      </div>

      <ConfirmDialog
        open={confirmRemoveScopes}
        title="Remove Scope Assignments"
        message={`Remove all scope assignments from role "${role.name}"? Entity, business unit and financial year defaults will be cleared.`}
        confirmLabel="Remove"
        cancelLabel="Cancel"
        variant="danger"
        loading={removingScopes}
        loadingMessage="Removing..."
        onConfirm={handleRemoveScopes}
        onCancel={() => setConfirmRemoveScopes(false)}
      />
    </>
  );
};

export default RoleViewModal;
